import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { TAXONOMY } from "@/data/taxonomy";

const MAX_CATEGORIES = 10;

export default function CategoryMultiPicker({ value, onChange, testIdPrefix = "category" }) {
  const [open, setOpen] = useState(false);

  const toggle = (leaf) => {
    if (value.includes(leaf)) {
      onChange(value.filter((c) => c !== leaf));
      return;
    }
    if (value.length >= MAX_CATEGORIES) return;
    onChange([...value, leaf]);
  };

  const remove = (leaf) => onChange(value.filter((c) => c !== leaf));

  const atMax = value.length >= MAX_CATEGORIES;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between rounded-[35px] font-normal"
            data-testid={`${testIdPrefix}-multi-trigger`}
          >
            <span className={value.length === 0 ? "text-gray-400" : ""}>
              {value.length === 0
                ? "Search & pick categories…"
                : `${value.length} / ${MAX_CATEGORIES} selected`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]"
          align="start"
          data-testid={`${testIdPrefix}-multi-popover`}
        >
          <Command
            filter={(itemValue, search) => {
              // itemValue is "Group::Item" or "Group::__general__"
              const [group, leaf] = itemValue.split("::");
              const haystack = `${group} ${leaf === "__general__" ? "" : leaf}`.toLowerCase();
              return haystack.includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput
              placeholder="Type to search categories…"
              data-testid={`${testIdPrefix}-multi-search`}
            />
            <CommandList className="max-h-80">
              <CommandEmpty>No category matches.</CommandEmpty>
              {TAXONOMY.map((t, idx) => (
                <div key={t.group}>
                  {idx > 0 && <CommandSeparator />}
                  <CommandGroup heading={t.group}>
                    <CommandItem
                      value={`${t.group}::__general__`}
                      onSelect={() => toggle(t.group)}
                      disabled={!value.includes(t.group) && atMax}
                      data-testid={`${testIdPrefix}-multi-option-${t.group}`}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${value.includes(t.group) ? "opacity-100" : "opacity-0"}`}
                      />
                      <span className="font-medium">{t.group}</span>
                      <span className="ml-1 text-xs text-gray-500">(general)</span>
                    </CommandItem>
                    {t.items.map((leaf) => (
                      <CommandItem
                        key={leaf}
                        value={`${t.group}::${leaf}`}
                        onSelect={() => toggle(leaf)}
                        disabled={!value.includes(leaf) && atMax}
                        data-testid={`${testIdPrefix}-multi-option-${leaf}`}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${value.includes(leaf) ? "opacity-100" : "opacity-0"}`}
                        />
                        <span className="pl-3">{leaf}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </div>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex flex-wrap gap-2 min-h-[2rem]" data-testid={`${testIdPrefix}-multi-chips`}>
        {value.length === 0 && (
          <p className="text-xs text-gray-400">
            No categories selected yet — pick up to {MAX_CATEGORIES}.
          </p>
        )}
        {value.map((c) => (
          <Badge
            key={c}
            variant="outline"
            className="rounded-sm gap-1 pr-1 text-xs"
            data-testid={`${testIdPrefix}-chip-${c}`}
          >
            {c}
            <button
              type="button"
              onClick={() => remove(c)}
              className="ml-1 rounded-sm hover:bg-gray-100 p-0.5"
              data-testid={`${testIdPrefix}-chip-remove-${c}`}
              aria-label={`Remove ${c}`}
            >
              <X size={12} />
            </button>
          </Badge>
        ))}
      </div>
      {value.length > 0 && (
        <p className="text-xs text-gray-500">
          {value.length} / {MAX_CATEGORIES} selected
        </p>
      )}
    </div>
  );
}
